// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallVariationMargin } from '@hooks/mutations/collateral/useCallVariationMargin';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import { CollateralAgreement } from '@daml.js/utility-collateral-app-v2/lib/Utility/Collateral/App/Model/Collateral/module';
import { CollateralState } from '@daml.js/utility-collateral-app-v2/lib/Utility/Collateral/App/Model/State/module';
import { ContractId } from '@daml/types';
import FormDialog from '../../../../../components/Dialog/FormDialog';
import TextInput, { TextInputLabel } from '../../../../../components/Form/TextInput';
import { MarketDataForm } from '../../../common/marketData/MarketDataForm';
import { useConvertToMarketData } from '../../../common/marketData/useConvertToMarketData';
import { useGetCollateralAgreement } from '../../../common/useGetCollateralAgreement';
import { useGetEligibleInstrumentsFromCollateralAgreement } from '../../../common/useGetEligibleInstrumentsFromCollateralAgreement';
import { useCallVariationMarginForm } from './useCallVariationMarginForm';

export type CallVariationMarginDialogProps = {
  collateralAgreementCId: ContractId<CollateralAgreement>;
  collateralStateCId: ContractId<CollateralState>;
  onClose: () => void;
  open: boolean;
};

export const CallVariationMarginDialog = ({
  collateralAgreementCId,
  collateralStateCId,
  onClose,
  open,
}: CallVariationMarginDialogProps) => {
  const callVariationMargin = useCallVariationMargin();

  const { getCollateralAgreementByContractId } = useGetCollateralAgreement();

  const collateralAgreement = useMemo(() => {
    const agreement = getCollateralAgreementByContractId(collateralAgreementCId);

    if (!agreement) {
      throw new Error(`Collateral agreement not found for contract Id: ${collateralAgreementCId}!`);
    }

    return agreement;
  }, [getCollateralAgreementByContractId, collateralAgreementCId]);

  const eligibleInstruments = useGetEligibleInstrumentsFromCollateralAgreement({
    collateralAgreement,
  });

  const form = useCallVariationMarginForm(collateralAgreement);
  const convertToMarketData = useConvertToMarketData();

  const onSubmit = useCallback(async () => {
    const createdAt = dayjs().toISOString();

    await callVariationMargin({
      cid: collateralAgreement.contractId,
      argument: {
        reference: form.reference,
        createdAt,
        exposureAmount: form.exposureAmount,
        collateralStateCid: collateralStateCId,
        marketData: convertToMarketData(form.marketData, createdAt),
        recipient: collateralAgreement.payload.partyB,
      },
    });

    onClose();
  }, [
    callVariationMargin,
    convertToMarketData,
    collateralAgreement,
    collateralStateCId,
    form,
    onClose,
  ]);

  return (
    <FormDialog
      submitName="Call"
      onCancel={onClose}
      open={open}
      title="Call Variation Margin"
      onSubmit={onSubmit}
      isSubmitDisabled={form.isSubmitDisabled}
    >
      <TextInput
        enableTopLabel
        placeholder="Enter an identifier for this margin call"
        label="Reference"
        value={form.reference}
        setValue={form.setReference}
      />
      <TextInput
        enableTopLabel
        placeholder="Enter an exposure amount"
        label="Exposure amount"
        value={form.exposureAmount}
        setValue={form.setExposureAmount}
        type="number"
      />
      <Box sx={{ mb: 1 }}>
        <TextInputLabel label="Market Data" required />
        <MarketDataForm
          value={form.marketData}
          onAdd={form.addPricingData}
          onUpdate={form.updatePricingData}
          onRemove={form.removePricingData}
          eligibleInstruments={eligibleInstruments}
        />
      </Box>
    </FormDialog>
  );
};
